import React from "react";
import { useWatch } from "react-hook-form";

import { FormInput } from "components/ui/FormInput";
import { ROBOTS_OPTIONS, SITE_CATALOG_URL } from "./seoHelpers";

const NEW_PRODUCT_JSON_LD_MESSAGE =
  "JsonLD data will be prepared automatically once you will submit the product details.";

const plainText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const Counter = ({ value, max }) => (
  <small className={value > max ? "text-danger" : "text-muted"}>
    {value} / {max} characters
  </small>
);

// The "SEO" tab of the Add / Edit product pages. Every field is stored with the product and used on
// that product's page. An empty field means "use the automatic value".
export const ProductSeoPanel = ({ register, control, setValue, isEdit, originalSlug }) => {
  const watched = useWatch({
    control,
    name: ["heading", "description", "seoSlug", "seoTitle", "metaDescription", "customJs"],
  });
  const [heading, description, seoSlug, seoTitle, metaDescription, customJs] = watched || [];

  const previewTitle = seoTitle || heading || "Product name";
  const previewSlug = seoSlug || originalSlug || "product-url";
  const previewDescription =
    metaDescription || plainText(description).slice(0, 160) || "The product description appears here.";

  const slugChanged = isEdit && originalSlug && seoSlug && seoSlug.trim() !== originalSlug;

  return (
    <div className="product_seo_panel">
      {/* ---------- preview ---------- */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="mb-3">Search result preview</h6>
          <div style={{ maxWidth: 640 }}>
            <div style={{ color: "#4d5156", fontSize: 13, wordBreak: "break-all" }}>
              {SITE_CATALOG_URL}
              {previewSlug}
            </div>
            <div style={{ color: "#1a0dab", fontSize: 20, lineHeight: 1.3 }}>{previewTitle}</div>
            <div style={{ color: "#4d5156", fontSize: 14 }}>{previewDescription}</div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* ---------- URL ---------- */}
        <div className="mb-3 col-12">
          <label htmlFor="seoSlug" className="form-label">
            Product URL (slug)
          </label>
          <div className="input-group">
            <span className="input-group-text">{SITE_CATALOG_URL}</span>
            <FormInput
              type="text"
              name="seoSlug"
              placeholder={isEdit ? "product-url" : "Leave empty to create it from the product name"}
              {...register("seoSlug")}
            />
          </div>
          {slugChanged ? (
            <small className="text-warning d-block mt-1">
              You are changing a published URL. Search engines already know the old one. It will keep working and
              redirect to the new URL, and you will be asked to confirm when you save.
            </small>
          ) : (
            <small className="text-muted d-block mt-1">
              {isEdit
                ? "This URL never changes when the product is saved. Only edit it here if you really need to."
                : "Use lowercase letters, numbers and hyphens. Leave empty to create it from the product name."}
            </small>
          )}
        </div>

        {/* ---------- meta title / description ---------- */}
        <div className="mb-3 col-md-6">
          <label htmlFor="seoTitle" className="form-label">
            Meta title
          </label>
          <FormInput
            type="text"
            name="seoTitle"
            placeholder="Empty: the product name is used"
            {...register("seoTitle")}
          />
          <Counter value={(seoTitle || "").length} max={60} />
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="metaKeywords" className="form-label">
            Meta keywords
          </label>
          <FormInput
            type="text"
            name="metaKeywords"
            placeholder="keyword one, keyword two, keyword three"
            {...register("metaKeywords")}
          />
          <small className="text-muted">Separate keywords with commas.</small>
        </div>
        <div className="mb-3 col-12">
          <label htmlFor="metaDescription" className="form-label">
            Meta description
          </label>
          <textarea
            name="metaDescription"
            className="form-control"
            rows={3}
            placeholder="Empty: the first 160 characters of the product description are used"
            {...register("metaDescription")}
          />
          <Counter value={(metaDescription || "").length} max={160} />
        </div>

        {/* ---------- robots / canonical ---------- */}
        <div className="mb-3 col-md-6">
          <label htmlFor="metaRobots" className="form-label">
            Robots (search engine visibility)
          </label>
          <select
            name="metaRobots"
            className="form-select form-control"
            defaultValue="index,follow"
            {...register("metaRobots")}
          >
            {ROBOTS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="canonicalUrl" className="form-label">
            Canonical URL (override)
          </label>
          <FormInput
            type="text"
            name="canonicalUrl"
            placeholder="Empty: the product's own URL is used"
            {...register("canonicalUrl")}
          />
          <small className="text-muted">Only set this when another page is the main version of this product.</small>
        </div>

        {/* ---------- social sharing ---------- */}
        <div className="col-12">
          <h6 className="mt-2 mb-3">Social sharing (Open Graph)</h6>
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="ogTitle" className="form-label">
            Social title
          </label>
          <FormInput
            type="text"
            name="ogTitle"
            placeholder="Empty: the meta title is used"
            {...register("ogTitle")}
          />
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="ogImage" className="form-label">
            Social image (URL)
          </label>
          <FormInput
            type="text"
            name="ogImage"
            placeholder="Empty: the product image is used"
            {...register("ogImage")}
          />
          <small className="text-muted">Full image address, for example https://aapharmasyn.com/.../image.jpg</small>
        </div>
        <div className="mb-3 col-12">
          <label htmlFor="ogDescription" className="form-label">
            Social description
          </label>
          <textarea
            name="ogDescription"
            className="form-control"
            rows={2}
            placeholder="Empty: the meta description is used"
            {...register("ogDescription")}
          />
        </div>

        {/* ---------- structured data ---------- */}
        <div className="col-12">
          <h6 className="mt-2 mb-3">Structured data (JSON-LD)</h6>
        </div>
        <div className="mb-3 col-12">
          <label htmlFor="jsonLd" className="form-label">
            JSON-LD
          </label>
          {isEdit ? (
            <>
              <textarea
                name="jsonLd"
                className="form-control"
                style={{ fontFamily: "monospace", fontSize: 13 }}
                rows={16}
                spellCheck={false}
                {...register("jsonLd")}
              />
              <div className="d-flex justify-content-between align-items-start mt-1">
                <small className="text-muted">
                  The price, stock and URL inside this data are refreshed automatically whenever the product is saved
                  or its stock status changes. Everything else stays exactly as you write it.
                </small>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary ms-3"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => setValue && setValue("jsonLd", "", { shouldDirty: true })}
                >
                  Prepare automatically
                </button>
              </div>
              <small className="text-muted d-block">
                "Prepare automatically" empties the box; the data is then rebuilt from the product details when you
                save.
              </small>
            </>
          ) : (
            <>
              <textarea className="form-control" rows={3} disabled value="" readOnly />
              <small className="text-muted d-block mt-1">{NEW_PRODUCT_JSON_LD_MESSAGE}</small>
            </>
          )}
        </div>

        {/* ---------- custom JavaScript ---------- */}
        <div className="col-12">
          <h6 className="mt-2 mb-3">Custom JavaScript (optional)</h6>
        </div>
        <div className="mb-3 col-12">
          <div className="alert alert-warning py-2">
            This code runs for <strong>every visitor</strong> of this product page. Only add code you trust and have
            tested. Paste the JavaScript only, without the &lt;script&gt; tags.
          </div>
          <textarea
            name="customJs"
            className="form-control"
            style={{ fontFamily: "monospace", fontSize: 13 }}
            rows={8}
            spellCheck={false}
            placeholder="// optional JavaScript for this product only"
            {...register("customJs")}
          />
          {customJs ? <small className="text-muted">{customJs.length} characters</small> : null}
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="customJsPosition" className="form-label">
            Where to run it
          </label>
          <select
            name="customJsPosition"
            className="form-select form-control"
            defaultValue="bottom"
            {...register("customJsPosition")}
          >
            <option value="bottom">At the end of the page (recommended)</option>
            <option value="top">At the top of the page</option>
          </select>
        </div>
      </div>
    </div>
  );
};
